/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { Modal } from '@carbon/react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Children, useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import trackComponentEvent from '../../../Utilities/analytics/analytics.js';
import canUseDom from '../../../Utilities/canUseDom/canUseDom.js';
import { noopFn, returnsTrueFn } from '../../../Utilities/propHelpers/defaultProps.js';
import { getPanelId, getPanelDetails } from './utils/getPanelDetails.js';

var _excluded = ["isMultiStep", "className", "isOpen", "hasOverlay", "children", "getPageRef", "setPageRef", "panelSize", "title", "focusOnCloseSelector", "data-testid", "modalOnDismiss", "dismissalModalHeader", "dismissalModalBody", "dismissalModalLabel", "analytics", "afterClose", "onBreadCrumbClick", "onCancelClick", "onCloseClick", "onDoneClick", "onNextClick", "onPreviousClick", "noRenderWhileClosed"];

/**
 * Adds a scroll lock to the body to prevent scrolling.
 */
var addScrollLock = function addScrollLock() {
  return document.body.classList.add('cpc--no-scroll');
};

/**
 * Removes the scroll lock from the body.
 */
var removeScrollLock = function removeScrollLock() {
  return document.body.classList.remove('cpc--no-scroll');
};

/**
 * Manages the focus state for the initial focused element.
 */
var useInitialFocus = function useInitialFocus(selector) {
  var initialFocus = useRef();
  var selectorFocus = useRef(null);
  if (selectorFocus.current === null && selector && canUseDom) {
    selectorFocus.current = document.getElementById(selector);
  }
  var focusManagement = useRef({
    focus: function focus() {
      if (document !== 'undefined' && document.body.contains(initialFocus.current)) {
        initialFocus.current.focus();
      }
    },
    setFocus: function setFocus() {
      initialFocus.current = document !== 'undefined' ? document.activeElement : null;
    }
  });
  return selector ? selectorFocus.current : focusManagement.current;
};

/**
 * A custom hook to return the previous state.
 */
var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};
var SidePanelContainer = function SidePanelContainer(_ref) {
  var _ref$isMultiStep = _ref.isMultiStep,
    isMultiStep = _ref$isMultiStep === void 0 ? false : _ref$isMultiStep,
    className = _ref.className,
    _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === void 0 ? true : _ref$isOpen,
    _ref$hasOverlay = _ref.hasOverlay,
    hasOverlay = _ref$hasOverlay === void 0 ? true : _ref$hasOverlay,
    children = _ref.children,
    _ref$getPageRef = _ref.getPageRef,
    getPageRef = _ref$getPageRef === void 0 ? noopFn : _ref$getPageRef,
    _ref$setPageRef = _ref.setPageRef,
    setPageRef = _ref$setPageRef === void 0 ? noopFn : _ref$setPageRef,
    _ref$panelSize = _ref.panelSize,
    panelSize = _ref$panelSize === void 0 ? 'medium' : _ref$panelSize,
    title = _ref.title,
    _ref$focusOnCloseSele = _ref.focusOnCloseSelector,
    focusOnCloseSelector = _ref$focusOnCloseSele === void 0 ? '' : _ref$focusOnCloseSele,
    testId = _ref['data-testid'],
    _ref$modalOnDismiss = _ref.modalOnDismiss,
    modalOnDismiss = _ref$modalOnDismiss === void 0 ? false : _ref$modalOnDismiss,
    dismissalModalHeader = _ref.dismissalModalHeader,
    dismissalModalBody = _ref.dismissalModalBody,
    dismissalModalLabel = _ref.dismissalModalLabel,
    analytics = _ref.analytics,
    _ref$afterClose = _ref.afterClose,
    afterCloseProp = _ref$afterClose === void 0 ? returnsTrueFn : _ref$afterClose,
    _ref$onBreadCrumbClic = _ref.onBreadCrumbClick,
    onBreadCrumbClick = _ref$onBreadCrumbClic === void 0 ? returnsTrueFn : _ref$onBreadCrumbClic,
    _ref$onCancelClick = _ref.onCancelClick,
    onCancelClick = _ref$onCancelClick === void 0 ? returnsTrueFn : _ref$onCancelClick,
    _ref$onCloseClick = _ref.onCloseClick,
    onCloseClick = _ref$onCloseClick === void 0 ? returnsTrueFn : _ref$onCloseClick,
    _ref$onDoneClick = _ref.onDoneClick,
    onDoneClick = _ref$onDoneClick === void 0 ? returnsTrueFn : _ref$onDoneClick,
    _ref$onNextClick = _ref.onNextClick,
    onNextClick = _ref$onNextClick === void 0 ? returnsTrueFn : _ref$onNextClick,
    _ref$onPreviousClick = _ref.onPreviousClick,
    onPreviousClick = _ref$onPreviousClick === void 0 ? returnsTrueFn : _ref$onPreviousClick,
    noRenderWhileClosed = _ref.noRenderWhileClosed,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;

  // Consolidate remaining props along with defaulted props that must be passed to children
  var containerPanelProps = _objectSpread2(_objectSpread2({}, props), {}, {
    onBreadCrumbClick: onBreadCrumbClick,
    onCancelClick: onCancelClick,
    onCloseClick: onCloseClick,
    onDoneClick: onDoneClick,
    onNextClick: onNextClick,
    onPreviousClick: onPreviousClick
  });
  var sidePanels = Children.toArray(children);
  var _useState = useState(!isOpen),
    _useState2 = _slicedToArray(_useState, 2),
    preventRendering = _useState2[0],
    setPreventRendering = _useState2[1];
  // Shared State
  var _useState3 = useState(isOpen),
    _useState4 = _slicedToArray(_useState3, 2),
    panelsOpen = _useState4[0],
    setPanelsOpen = _useState4[1];
  var _useState5 = useState(getPanelId(sidePanels[0], 0)),
    _useState6 = _slicedToArray(_useState5, 2),
    activePanelId = _useState6[0],
    setActivePanelById = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    modalOpen = _useState8[0],
    setModalOpen = _useState8[1];
  var _useState9 = useState(function () {
      return function () {
        return true;
      };
    }),
    _useState10 = _slicedToArray(_useState9, 2),
    modalCloseFunc = _useState10[0],
    setModalCloseFunc = _useState10[1];

  // Refs
  var childCloseRef = useRef();
  var initialFocus = useInitialFocus(focusOnCloseSelector);
  var timeoutRef = useRef(null);
  var animationsOn = useRef();

  // Resets the active panel to the first side panel after the animations finish.
  var resetActivePanel = useCallback(function (postCloseFunction) {
    if (!timeoutRef.current) {
      var ref;
      var timeoutValue = animationsOn.current ? 400 : 0;
      var faded = new Promise(function (resolveFaded) {
        ref = setTimeout(function () {
          setActivePanelById(getPanelId(sidePanels[0], 0));
          timeoutRef.current = null;
          resolveFaded();
          postCloseFunction === null || postCloseFunction === void 0 || postCloseFunction();
        }, timeoutValue);
      });
      timeoutRef.current = ref;
      return faded;
    }
    return null;
  }, [sidePanels]);
  var prevIsOpen = usePrevious(isOpen);

  // Clear the timeout if we're unmounting.
  useEffect(function () {
    var _window;
    // // Used to match the media-query in our CSS
    animationsOn.current = (_window = window) === null || _window === void 0 || (_window = _window.matchMedia('(prefers-reduced-motion: no-preference)')) === null || _window === void 0 ? void 0 : _window.matches;
    return function () {
      return timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, []);
  useEffect(function () {
    var eventPayload = {
      action: 'SidePanelContainer Open',
      title: typeof title === 'string' ? title : '',
      // TODO: include a prop for analytics title for node titles?
      palComponent: 'SidePanelContainer',
      category: 'Offering Interface'
    };
    if (isOpen && !prevIsOpen && analytics) {
      trackComponentEvent('User Form', eventPayload);
    } else if (!isOpen && prevIsOpen && analytics) {
      trackComponentEvent('User Form', _objectSpread2(_objectSpread2({}, eventPayload), {}, {
        action: 'SidePanelContainer Close'
      }));
    }
  }, [isOpen, prevIsOpen, title, analytics]);

  // TODO: Deprecate these externally facing refs in the next release of PAL.
  useEffect(function () {
    if (getPageRef) {
      getPageRef({
        activePanelId: activePanelId,
        index: sidePanels.findIndex(function (panel, index) {
          return activePanelId === getPanelId(panel, index);
        })
      });
    }
  }, [isOpen, activePanelId, sidePanels]); // eslint-disable-line react-hooks/exhaustive-deps

  if (setPageRef) {
    setPageRef(function (index) {
      if (sidePanels[index]) {
        setActivePanelById(getPanelId(sidePanels[index], index));
      }
    });
  }
  var afterClose = useCallback(function () {
    afterCloseProp();
    setPreventRendering(true);
  }, [afterCloseProp]);
  useEffect(function () {
    // Reset the panel ID to the first panel if we're closing the panels and refocus on the initial element.
    if (!isOpen && panelsOpen) {
      resetActivePanel(afterClose);
    }

    // Only refocus if there is an overlay and we're changing the panels.
    if (!isOpen && panelsOpen && hasOverlay) {
      initialFocus.focus();
    }
  }, [afterClose, hasOverlay, initialFocus, isOpen, panelsOpen, resetActivePanel]);

  // Watch for changes to isOpen to set the internal state and set focus.
  useEffect(function () {
    setPanelsOpen(isOpen);
    if (isOpen) setPreventRendering(false);
    if (isOpen && !focusOnCloseSelector) {
      initialFocus.setFocus();
    }
    if (isOpen && focusOnCloseSelector) {
      initialFocus.focus();
    }
  }, [isOpen, initialFocus, focusOnCloseSelector]);

  // Add or remove scroll locking from the body.
  useEffect(function () {
    if (panelsOpen && hasOverlay) {
      addScrollLock();
    } else {
      removeScrollLock();
    }
    return removeScrollLock;
  }, [panelsOpen, hasOverlay]);

  // Closes the panel and reasserts focus on the initially focused element.
  var closePanel = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setPanelsOpen(false);
            _context.next = 3;
            return resetActivePanel(afterClose);
          case 3:
            if (hasOverlay) {
              initialFocus.focus();
            }
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function closePanel() {
      return _ref2.apply(this, arguments);
    };
  }();

  // Closes the side panel and calls the child panels close event.
  var handleOverlayEvents = function handleOverlayEvents(event) {
    var _event$target;
    var escapeKeyPress = event.key === 'Escape' && hasOverlay;
    var overlayClick = hasOverlay && ((_event$target = event.target) === null || _event$target === void 0 || (_event$target = _event$target.dataset) === null || _event$target === void 0 ? void 0 : _event$target.id) === 'cpc-side-panel-container';
    if ((escapeKeyPress || overlayClick) && childCloseRef.current(event, true)) {
      closePanel();
    }
  };

  // Side Panel Basic Component classes
  var panelContainerClasses = classnames('cpc--side-panel-container', {
    'cpc--side-panel-container--open': panelsOpen,
    'cpc--side-panel-container--overlay': hasOverlay
  }, className);
  var onCloseWithModalHandler = function onCloseWithModalHandler(closeFunc) {
    setModalOpen(true);
    setModalCloseFunc(function () {
      return function () {
        if (closeFunc()) closePanel(); // behave like the typical flow in handleOverlayEvents above
      };
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    "data-id": "cpc-side-panel-container",
    className: panelContainerClasses,
    onMouseDown: handleOverlayEvents,
    onKeyDown: handleOverlayEvents,
    "data-testid": testId
  }, (!preventRendering || !noRenderWhileClosed) && /*#__PURE__*/React.createElement("div", {
    className: "cpc--side-panel-container__panels"
  }, sidePanels.reduce(function (visiblePanels, panel, index, panels) {
    var _getPanelDetails = getPanelDetails(panel, index, activePanelId, panels),
      id = _getPanelDetails.id,
      nextId = _getPanelDetails.nextId,
      previousId = _getPanelDetails.previousId,
      isActive = _getPanelDetails.isActive,
      isNextActive = _getPanelDetails.isNextActive,
      isPreviousActive = _getPanelDetails.isPreviousActive,
      isNestedActive = _getPanelDetails.isNestedActive;
    if (isActive || isNextActive || isNestedActive || isPreviousActive) {
      return [].concat(_toConsumableArray(visiblePanels), [/*#__PURE__*/React.cloneElement(panel, _objectSpread2(_objectSpread2(_objectSpread2({}, containerPanelProps), panel.props), {}, {
        onCloseWithModalHandler: onCloseWithModalHandler,
        key: id,
        id: id,
        isMultiStep: isMultiStep,
        internal: {
          containerPanelProps: containerPanelProps,
          panelSize: panelSize,
          isActive: isActive,
          activePanelId: activePanelId,
          setActivePanelById: setActivePanelById,
          nextId: nextId,
          previousId: previousId,
          closePanel: closePanel,
          childCloseRef: childCloseRef,
          hasOverlay: hasOverlay,
          renderPrimary: true,
          isOpen: (isActive || isNestedActive || isNextActive) && panelsOpen,
          modalOnDismiss: modalOnDismiss
        }
      }))]);
    }
    return visiblePanels;
  }, []))), modalOnDismiss && /*#__PURE__*/React.createElement(Modal, {
    modalHeading: dismissalModalHeader || t('Closing side panel')
    // elements with role 'presentation' should not have an aria-label
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/presentation_role
    // aria-label={dismissalModalLabel || translate('modal.label')}
    ,
    modalLabel: dismissalModalLabel || t('Are you sure?'),
    onRequestClose: function onRequestClose() {
      return setModalOpen(false);
    },
    onRequestSubmit: function onRequestSubmit() {
      setModalOpen(false);
      modalCloseFunc();
    },
    secondaryButtonText: t('Cancel'),
    primaryButtonText: t('Close'),
    danger: true,
    open: modalOpen
  }, dismissalModalBody || t('Are you sure you want to close the side panel?')));
};
var validSizes = function validSizes(props, propName, componentName) {
  var size = props[propName];
  if (size) {
    if (typeof size === 'string') {
      return size === 'xl' || size === 'medium' || size === 'small' ? null : new Error("".concat(propName, " in ").concat(componentName, " must be \"small\", \"medium\" or \"xl\". The \"large\" option is the same size as the \"medium\". The \"large\" prop will be deprecated in the next major release"));
    }
    return new Error("".concat(propName, " in ").concat(componentName, " valid string."));
  }
  return null;
};
SidePanelContainer.propTypes = {
  /**
   * A class name to provide to the side panel container component.
   */
  className: PropTypes.string,
  /**
   *  A boolean value indicating whether or not the SidePanel should be open.
   */
  isOpen: PropTypes.bool,
  /**
   * If provided this title will be used for all of the rendered side panels, unless this is
   * overridden by the props provided to a SidePanel. Elements or strings can be rendered inside of the title prop.
   */
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /**
   * A boolean value that specifies whether or not everything outside of the SidePanel should be
   * covered with a semi-transparent overlay.
   */
  hasOverlay: PropTypes.bool,
  /**
   * Enables Multi Step Side Panel with Progress Indicator,
   */
  isMultiStep: PropTypes.bool,
  /**
   * The text for the default button when there is only one SidePanel, or the last panel is displayed
   * The default value can be found in /src/locales/(en|es)/SidePanel
   */
  doneText: PropTypes.string,
  /**
   * An ID used for testing purposes on the container element.
   */
  'data-testid': PropTypes.string,
  /**
   * the text for the "cancel" button, used when on the only or first panel of a set. If not provided, this will be inherited
   * from the parent sidePanelContainer.
   */
  closePanelText: PropTypes.string,
  /**
   * The text for the primary button for any panels after other than the last when there are multiple panels
   * The default value can be found in /src/locales/(en|es)/SidePanel
   */
  nextText: PropTypes.string,
  /**
   * The size of the panel. Valid values are 'medium', 'small' and 'xl'. Default is 'medium'.
   * The option "large" will be deprecated in the next major release.
   */
  panelSize: validSizes,
  /**
   * Prop to set focus when the SidePanel is closed. The required id should be passed and then on close this
   * component will be the one focused.
   */
  focusOnCloseSelector: PropTypes.string,
  /**
   * A function to be called if the user clicks the cancel button. If this function returns false
   * the panel will not execute the default navigation behavior. This can be used for form validation.
   * This will be passed to the active SidePanel if no onCancelClick prop is provided and will be used
   * in its place.
   */
  onCancelClick: PropTypes.func,
  /**
   * A function to be called if the user clicks the close button (x) in the upper right of the panel.
   * This will be passed to the active SidePanel if no onCloseClick prop is provided and will be used
   * in its place.
   */
  onCloseClick: PropTypes.func,
  /**
   * A function to be called if the user clicks the done button.  If this function returns false
   * the panel will not execute the default navigation behavior. This can be used for form validation.
   * This will be passed to the active SidePanel if no onDoneClick prop is provided and will be used
   * in its place.
   */
  onDoneClick: PropTypes.func,
  /**
   * A function to be called if the user clicks a breadcrumb.  If this function returns false
   * the panel will not execute the default navigation behavior. This can be used for form validation.
   */
  onBreadCrumbClick: PropTypes.func,
  /**
   * A function to be called when the user clicks the primary button. If this function returns false
   * the panel will not execute the default navigation behavior. This can be used for form validation.
   * This will be passed to the active SidePanel if no onNextClick prop is provided and will be used
   * in its place.
   */
  onNextClick: PropTypes.func,
  /**
   * A function to be called when the user clicks the secondary button. If this function returns false
   * the panel will not execute the default navigation behavior. This can be used for form validation.
   * This will be passed to the active SidePanel if no onPreviousClick prop is provided and will be used
   * in its place.
   */
  onPreviousClick: PropTypes.func,
  /**
   * A function to be called to return the getPage function. The getPage function will return the
   * current panel id.
   */
  getPageRef: PropTypes.func,
  /**
   * A function to be called to return the setPage function. The setPage function will accept page id
   * parameter to switch the specified panel id.
   */
  setPageRef: PropTypes.func,
  /**
   * The children of the SidePanelContainer. These should only be SidePanel elements.
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  /**
   * If this property is true, the bottom nav will not be rendered.
   */
  hideBottomNav: PropTypes.bool,
  /**
   * If true, a warning will appear if the user tries to click away that
   * informs them that everything in the modal will be lost
   */
  modalOnDismiss: PropTypes.bool,
  /**
   * Header for dismissal warning modal
   */
  dismissalModalHeader: PropTypes.node,
  /**
   * Body for dismissal warning modal
   */
  dismissalModalBody: PropTypes.node,
  /**
   * Label for dismissal warning modal
   */
  dismissalModalLabel: PropTypes.node,
  /**
   * This optional prop allows the component to be tracked. By default the boolean is false and the component will not be tracked.
   * If you want to track this component's usage set the boolean to be true.
   */
  analytics: PropTypes.bool,
  /**
   * Function to call after the SidepanelContainer has fully closed, post-animation. If using a boolean statement to control rendering, this callback would be a good place to toggle it to false.
   */
  afterClose: PropTypes.func,
  /**
   * Completely stop rendering anything inside of SidePanelContainer if this is true and the sidepanel is not visible (including animations).
   */
  noRenderWhileClosed: PropTypes.bool
};
var SidePanelContainer$1 = SidePanelContainer;

export { SidePanelContainer$1 as default };
